import { del, get, post, put } from './fetch';

let store;

export function setStore(vuexStore) {
  store = vuexStore;
}

async function ResponseAdapter(response) {
  switch (response.status) {
    case 200:
    case 400:
    case 401:
    case 403:
    case 404:
    case 409:
      try {
        let currentUser = response.headers.get('Current-User');
        if (currentUser) {
          currentUser = JSON.parse(currentUser);
        }
        const parsedResponse = await response.json();
        return { status: response.status, data: parsedResponse, currentUser };
      } catch (e) {
        return {
          status: response.status,
          message: e.message,
        };
      } finally {
        if (response.status === 401) {
          store.dispatch('logout', { redirect: true });
        }
      }
    case 500:
      try {
        const parsedResponse = await response.json();
        return {
          status: response.status,
          data: {
            error: true,
            message: parsedResponse.message,
          },
        };
      } catch (e) {
        return {
          status: response.status,
          data: {
            error: true,
            message: e.message,
          },
        };
      }
    default:
      break;
  }

  return {
    status: response.status,
    message: response?.data?.message || 'UNKNOWN_DATA',
  };
}

export async function apiPostLogout() {
  return ResponseAdapter(await post('/api/v1/auth/logout'));
}
export async function apiDeleteAccount() {
  return ResponseAdapter(await del('/api/v1/auth/account'));
}
export async function apiPostChangePassword({ password }) {
  return ResponseAdapter(await post('/api/v1/auth/changepassword', { password }));
}

export async function apiGetTenant() {
  return ResponseAdapter(await get('/api/v1/tenant'));
}

export async function apiGetAddresses() {
  return ResponseAdapter(await get('/api/v1/tenant/subscription/addresslist'));
}
export async function apiPutAddress({ address_id, description, first_line, second_line, city, postal_code, region, country_code, status, custom_data }) {
  return ResponseAdapter(await put('/api/v1/tenant/subscription/address', { address_id, description, first_line, second_line, city, postal_code, region, country_code, status, custom_data }));
}
export async function apiChangeSubscriptionAddress({ address_id }) {
  return ResponseAdapter(await post('/api/v1/tenant/subscription/billingaddress', { address_id }));
}
export async function apiGetPayments() {
  return ResponseAdapter(await get('/api/v1/tenant/subscription/payments'));
}
export async function apiPostCancelSubscription() {
  return ResponseAdapter(await post('/api/v1/tenant/subscription/cancel'));
}
export async function apiPostPauseSubscription({ type, subscription_id, paused }) {
  return ResponseAdapter(await post('/api/v1/tenant/subscription/pause', { type, subscription_id, paused }));
}
export async function apiPostResumeSubscription() {
  return ResponseAdapter(await post('/api/v1/tenant/subscription/cancelscheduled'));
}
export async function apiPostUpdateSubscription({ price_id }) {
  return ResponseAdapter(await post('/api/v1/tenant/subscription/update', { price_id }));
}
export async function apiPostUpdatePreviewSubscription({ price_id }) {
  return ResponseAdapter(await post('/api/v1/tenant/subscription/preview', { price_id }));
}
export async function apiGetInvoice({ transaction_id }) {
  return ResponseAdapter(await get(`/api/v1/tenant/subscription/invoice?transaction_id=${transaction_id}`));
}

export async function apiPostConversationDefinition({ name, type, model_id, steps, transition }) {
  return ResponseAdapter(await post('/api/v1/conversation', { name, type, model_id, steps, transition }));
}

export async function apiGetDataTypeRelationship({ type, model_id }) {
  return ResponseAdapter(await get(`/api/v1/entity/relationships?type=${type}&model_id=${model_id}`));
}
export async function apiPutDataTypeRelationship({ type, model_id, relationships }) {
  return ResponseAdapter(await put('/api/v1/entity/relationships', { type, model_id, relationships }));
}

export async function apiPostSendSigninEmailLink({ email, token }) {
  const payload = { email, token };
  if (window.location.host.indexOf('local') === 0) {
    payload.local = true;
  }
  return ResponseAdapter(await post('/api/v1/auth/loginwithemail', payload));
}
export async function apiPostRegister({ password, name, timezone, picture, country, organisation, position, language }) {
  const payload = { password, name, timezone, picture, country, organisation, position, language };
  return ResponseAdapter(await post('/api/v1/auth/register', payload));
}
export async function apiPostResetPassword({ email, token, password }) {
  return ResponseAdapter(await post('/api/v1/auth/passwordreset', { email, token, password }));
}
export async function apiPostForgotPassword({ email, token, local = false }) {
  if (window.location.host.indexOf('local') === 0) {
    local = true;
  }
  return ResponseAdapter(await post('/api/v1/auth/forgotpassword', { email, token, local }));
}
export async function apiPostLoginWithPassword({ email, password, token }) {
  const payload = { email, password, token };
  return post('/api/v1/auth/loginwithpassword', payload);
}

export async function apiPostFeedback({ category, sentiment, message, fields, data, phrase }) {
  return ResponseAdapter(await post('/api/v1/feedback', { category, sentiment, message, fields, data, phrase }));
}

export async function apiPostEmailCardToUser(data) {
  return ResponseAdapter(await post('/api/v1/emailcardtouser', data));
}

export async function apiPutProfile(payload) {
  return ResponseAdapter(await put('/api/v1/tenant/profile', payload));
}

export async function apiGetAuthProviders() {
  return ResponseAdapter(await get(`/api/v1/auth/providers`));
}

export async function apiPutFlag({ key, value }) {
  return ResponseAdapter(await put(`/api/v1/flag`, { key, value }));
}

export async function apiGetUsers({ page_number = 0, pagesize = 10 }) {
  return ResponseAdapter(await get(`/api/v1/admin/users?pagesize=${pagesize}&pagenumber=${page_number}`));
}
export async function apiGetUserCount() {
  return ResponseAdapter(await get(`/api/v1/admin/users/count`));
}
/**
 * Loads the platform encryption key status (super admins only).
 * @returns {Promise<object>} Adapted response with checks, root keys, projects, problems, jobs and events.
 */
export async function apiGetAdminSecurityKeys() {
  return ResponseAdapter(await get(`/api/v1/admin/security/keys`));
}
/**
 * Registers the key a super admin put into slot A as root key generation 1 (super admins only).
 * @param {{fingerprint: string}} payload The fingerprint the admin confirmed; refused if the slot changed meanwhile.
 * @returns {Promise<object>} Adapted response with the registered root key's generation, slot and fingerprint.
 */
export async function apiPostAdminSecurityRootRegister({ fingerprint }) {
  return ResponseAdapter(await post(`/api/v1/admin/security/keys/root/register`, { fingerprint }));
}
/**
 * Switches to the new key in the free slot; project keys are re-wrapped in the background (super admins only).
 * @param {{fingerprint: string, rotate_project_keys: boolean}} payload The confirmed fingerprint, and whether to also rotate every project's key afterwards.
 * @returns {Promise<object>} Adapted response with the new root key.
 */
export async function apiPostAdminSecurityRootChange({ fingerprint, rotate_project_keys }) {
  return ResponseAdapter(await post(`/api/v1/admin/security/keys/root/change`, { fingerprint, rotate_project_keys }));
}
/**
 * Retires a root key generation that no project key uses any more (super admins only).
 * @param {{generation: number}} payload Generation to retire.
 * @returns {Promise<object>} Adapted response.
 */
export async function apiPostAdminSecurityRootRetire({ generation }) {
  return ResponseAdapter(await post(`/api/v1/admin/security/keys/root/retire`, { generation }));
}
/**
 * Starts a background key job across all projects (super admins only).
 * @param {{kind: string}} payload migrate, rotate or maintain.
 * @returns {Promise<object>} Adapted response with the started job.
 */
export async function apiPostAdminSecurityJob({ kind }) {
  return ResponseAdapter(await post(`/api/v1/admin/security/keys/jobs`, { kind }));
}
/**
 * Cancels the running key job (super admins only).
 * @returns {Promise<object>} Adapted response.
 */
export async function apiDeleteAdminSecurityJob() {
  return ResponseAdapter(await del(`/api/v1/admin/security/keys/jobs`));
}
export async function apiGetUser({ email }) {
  const response = await get(`/api/v1/admin/user?email=${email}`);
  return ResponseAdapter(response);
}
export async function apiPostAddUser({ email, password, name, timezone, country, organisation, position, language, flags }) {
  const response = await post(`/api/v1/admin/user`, { email, password, name, timezone, country, organisation, position, language, flags });
  return ResponseAdapter(response);
}
export async function apiPutUser({ email, name, timezone, country, organisation, position, language, flags }) {
  const response = await put(`/api/v1/admin/user`, { email, name, timezone, country, organisation, position, language, flags });
  return ResponseAdapter(response);
}

export async function apiGetProjectData(id) {
  return ResponseAdapter(await get(`/api/v1/project/data?project_id=${id}`));
}
export async function apiGetProjects() {
  return ResponseAdapter(await get('/api/v1/project/list'));
}
export async function apiGetProject(project_id) {
  return ResponseAdapter(await get(`/api/v1/project?project_id=${project_id}`));
}
export async function apiPostProject({ name }) {
  return ResponseAdapter(await post('/api/v1/project', { name }));
}
export async function apiPutProject({ project_id, name, brand_name, disabled, theme, authEmail, authEmailFrom, workflow_step_timeout }) {
  return ResponseAdapter(await put('/api/v1/project', { project_id, name, brand_name, disabled, theme, authEmail, authEmailFrom, workflow_step_timeout }));
}
export async function apiPutProjectDisabled({ project_id, disabled }) {
  return ResponseAdapter(await put('/api/v1/project/disabled', { project_id, disabled }));
}
export async function apiPostCheckProjectName({ name }) {
  return ResponseAdapter(await post('/api/v1/project/checkname', { name }));
}

export async function apiDeleteProject(project_id) {
  return ResponseAdapter(await del(`/api/v1/project?project_id=${project_id}`));
}

export async function apiGetProjectPersonas(project_id) {
  return ResponseAdapter(await get(`/api/v1/project/persona/list?project_id=${project_id}`));
}
export async function apiPostProjectPersona({ project_id, name, value }) {
  return ResponseAdapter(await post('/api/v1/project/persona', { project_id, name, value }));
}
export async function apiGetProjectPersona(project_id, persona_id) {
  return ResponseAdapter(await get(`/api/v1/project/persona?project_id=${project_id}&persona_id=${persona_id}`));
}
export async function apiPutProjectPersona({ project_id, persona_id, name, value }) {
  return ResponseAdapter(await put('/api/v1/project/persona', { project_id, persona_id, name, value }));
}
export async function apiDeleteProjectPersona(project_id, persona_id) {
  return ResponseAdapter(await del(`/api/v1/project/persona?project_id=${project_id}&persona_id=${persona_id}`));
}

export async function apiGetProjectEmailProviders(project_id) {
  return ResponseAdapter(await get(`/api/v1/project/email_provider/list?project_id=${project_id}`));
}
export async function apiPostProjectEmailProvider({ project_id, provider, config }) {
  return ResponseAdapter(await post('/api/v1/project/email_provider', { project_id, provider, config }));
}
export async function apiDeleteProjectEmailProvider(project_id, provider) {
  return ResponseAdapter(await del(`/api/v1/project/email_provider?project_id=${project_id}&provider=${provider}`));
}

/**
 * Loads the project's logging providers, the provider catalog and the logging options.
 * @param {string} project_id Project id.
 * @returns {Promise<object>} Adapted response with logging_providers, available_logging_providers and logging_options.
 */
export async function apiGetProjectLoggingProviders(project_id) {
  return ResponseAdapter(await get(`/api/v1/project/loggingprovider/list?project_id=${project_id}`));
}
/**
 * Creates or updates a project's logging provider. The server checks the key by sending one test log line.
 * @param {{project_id: string, provider: string, config: {region: string, key: string, enabled: boolean, options: object}}} payload Provider settings; config.key '********' keeps the stored key.
 * @returns {Promise<object>} Adapted response with the saved logging_provider, or a 400 with a readable message.
 */
export async function apiPostProjectLoggingProvider({ project_id, provider, config }) {
  return ResponseAdapter(await post('/api/v1/project/loggingprovider', { project_id, provider, config }));
}
/**
 * Removes a project's logging provider.
 * @param {string} project_id Project id.
 * @param {string} provider Provider key, e.g. NEW_RELIC.
 * @returns {Promise<object>} Adapted response.
 */
export async function apiDeleteProjectLoggingProvider(project_id, provider) {
  return ResponseAdapter(await del(`/api/v1/project/loggingprovider?project_id=${project_id}&provider=${provider}`));
}

export async function apiGetProjectAIProviders(project_id) {
  return ResponseAdapter(await get(`/api/v1/project/ai_provider/list?project_id=${project_id}`));
}
export async function apiPostProjectAIProvider(params) {
  return ResponseAdapter(await post('/api/v1/project/ai_provider', params));
}
export async function apiDeleteProjectAIProvider(project_id, provider) {
  return ResponseAdapter(await del(`/api/v1/project/ai_provider?project_id=${project_id}&provider=${provider}`));
}
export async function apiLMStudioModels(params) {
  return ResponseAdapter(await post('/api/v1/project/ai_provider/lmstudio/models', params));
}
export async function apiLMStudioSelectModel(params) {
  return ResponseAdapter(await post('/api/v1/project/ai_provider/lmstudio/select_model', params));
}
export async function apiLMStudioModelMeta(params) {
  return ResponseAdapter(await post('/api/v1/project/ai_provider/lmstudio/model_meta', params));
}

export async function apiGetProjectAgents(project_id) {
  return ResponseAdapter(await get(`/api/v1/project/agents?project_id=${project_id}`));
}

export async function apiGetProjectEndpoints(project_id) {
  return ResponseAdapter(await get(`/api/v1/project/endpoint/list?project_id=${project_id}`));
}
export async function apiGetProjectEndpoint(project_id, endpoint_id) {
  return ResponseAdapter(await get(`/api/v1/project/endpoint?project_id=${project_id}&endpoint_id=${endpoint_id}`));
}
export async function apiPostProjectEndpoint({ project_id, method, path, group_name, flow_id, request_params, authentication }) {
  return ResponseAdapter(await post('/api/v1/project/endpoint', { project_id, method, path, group_name, flow_id, request_params, authentication }));
}
export async function apiPutProjectEndpoint({ project_id, endpoint_id, method, path, group_name, flow_id, request_params, authentication }) {
  return ResponseAdapter(await put('/api/v1/project/endpoint', { project_id, endpoint_id, method, path, group_name, flow_id, request_params, authentication }));
}
export async function apiDeleteProjectEndpoint({ project_id, endpoint_id }) {
  return ResponseAdapter(await del(`/api/v1/project/endpoint?project_id=${project_id}&endpoint_id=${endpoint_id}`));
}

export async function apiGetProjectScheduledTasks(project_id) {
  return ResponseAdapter(await get(`/api/v1/project/scheduledtask/list?project_id=${project_id}`));
}
export async function apiGetProjectScheduledTask(project_id, task_id) {
  return ResponseAdapter(await get(`/api/v1/project/scheduledtask?project_id=${project_id}&task_id=${task_id}`));
}
export async function apiPostProjectScheduledTask({ project_id, frequency, schedule, hour, minute, flow_id }) {
  return ResponseAdapter(await post('/api/v1/project/scheduledtask', { project_id, frequency, schedule, hour, minute, flow_id }));
}
export async function apiPutProjectScheduledTask({ project_id, task_id, frequency, schedule, hour, minute, flow_id }) {
  return ResponseAdapter(await put('/api/v1/project/scheduledtask', { project_id, task_id, frequency, schedule, hour, minute, flow_id }));
}
export async function apiPutStartProjectScheduledTask({ project_id, task_id }) {
  return ResponseAdapter(await put('/api/v1/project/scheduledtask/start', { project_id, task_id }));
}
export async function apiPutStopProjectScheduledTask({ project_id, task_id }) {
  return ResponseAdapter(await put('/api/v1/project/scheduledtask/stop', { project_id, task_id }));
}
export async function apiDeleteProjectScheduledTask({ project_id, task_id }) {
  return ResponseAdapter(await del(`/api/v1/project/scheduledtask?project_id=${project_id}&task_id=${task_id}`));
}

export async function apiGetProjectFlows(project_id) {
  return ResponseAdapter(await get(`/api/v1/project/flow/list?project_id=${project_id}`));
}
export async function apiGetProjectFlow(project_id, flow_id) {
  return ResponseAdapter(await get(`/api/v1/project/flow?project_id=${project_id}&flow_id=${flow_id}`));
}
export async function apiPostProjectFlow({ project_id, name, steps, transition, flowType, agent }) {
  return ResponseAdapter(await post('/api/v1/project/flow', { project_id, name, steps, transition, flowType, agent }));
}
export async function apiPutProjectFlow({ project_id, flow_id, name, steps, transition, flowType, agent }) {
  return ResponseAdapter(await put('/api/v1/project/flow', { project_id, flow_id, name, steps, transition, flowType, agent }));
}
export async function apiDeleteProjectFlow(project_id, flow_id) {
  return ResponseAdapter(await del(`/api/v1/project/flow?project_id=${project_id}&flow_id=${flow_id}`));
}

export async function apiGetProjectDatatypes(project_id) {
  return ResponseAdapter(await get(`/api/v1/project/datatype/list?project_id=${project_id}`));
}
export async function apiGetProjectDatatype(project_id, name) {
  return ResponseAdapter(await get(`/api/v1/project/datatype?project_id=${project_id}&name=${name}`));
}
export async function apiPostProjectDatatype({ project_id, name, fields, colour }) {
  return ResponseAdapter(await post('/api/v1/project/datatype', { project_id, name, fields, colour }));
}
export async function apiPutProjectDatatype({ project_id, name, fields, colour }) {
  return ResponseAdapter(await put('/api/v1/project/datatype', { project_id, name, fields, colour }));
}
export async function apiDeleteProjectDatatype(project_id, name) {
  return ResponseAdapter(await del(`/api/v1/project/datatype?project_id=${project_id}&name=${name}`));
}

export async function apiGetProjectVariables(project_id) {
  return ResponseAdapter(await get(`/api/v1/project/variable/list?project_id=${project_id}`));
}
export async function apiGetProjectVariable(project_id, name) {
  return ResponseAdapter(await get(`/api/v1/project/variable?project_id=${project_id}&name=${name}`));
}
export async function apiPostProjectVariable({ project_id, name, dataType, value }) {
  return ResponseAdapter(await post('/api/v1/project/variable', { project_id, name, dataType, value }));
}
export async function apiPutProjectVariable({ project_id, name, dataType, value }) {
  return ResponseAdapter(await put('/api/v1/project/variable', { project_id, name, dataType, value }));
}
export async function apiDeleteProjectVariable({ project_id, name, dataType }) {
  return ResponseAdapter(await del(`/api/v1/project/variable?project_id=${project_id}&name=${name}&dataType=${dataType}`));
}

export async function apiGetProjectAuthentication(project_id) {
  return ResponseAdapter(await get(`/api/v1/project/authentication?project_id=${project_id}`));
}
export async function apiPutProjectAuthentication({ project_id, provider, config }) {
  return ResponseAdapter(await put(`/api/v1/project/authentication`, { project_id, provider, config }));
}

export async function apiGetProjectPublishList(project_id) {
  return ResponseAdapter(await get(`/api/v1/project/publish/list?project_id=${project_id}`));
}
export async function apiPostProjectPublish({ project_id, version }) {
  return ResponseAdapter(await post(`/api/v1/project/publish`, { project_id, version }));
}
export async function apiDeleteProjectPublish({ project_id, version, user }) {
  return ResponseAdapter(await del(`/api/v1/project/publish?project_id=${project_id}&version=${version}&user=${user}`));
}

export async function apiGetProjectSecrets(project_id) {
  return ResponseAdapter(await get(`/api/v1/project/secret/list?project_id=${project_id}`));
}
export async function apiPostProjectSecret({ project_id, name, value }) {
  return ResponseAdapter(await post('/api/v1/project/secret', { project_id, name, value }));
}
export async function apiDeleteProjectSecret({ project_id, name }) {
  return ResponseAdapter(await del(`/api/v1/project/secret?project_id=${project_id}&name=${name}`));
}

export async function apiGetProjectTemplates(project_id) {
  return ResponseAdapter(await get(`/api/v1/project/template/list?project_id=${project_id}`));
}
export async function apiGetProjectTemplate(project_id, template_id) {
  return ResponseAdapter(await get(`/api/v1/project/template?project_id=${project_id}&template_id=${template_id}`));
}
export async function apiPostProjectTemplate({ project_id, name, text, html, subject }) {
  return ResponseAdapter(await post('/api/v1/project/template', { project_id, name, text, html, subject }));
}
export async function apiPutProjectTemplate({ project_id, template_id, name, text, html, subject }) {
  return ResponseAdapter(await put('/api/v1/project/template', { project_id, template_id, name, text, html, subject }));
}
export async function apiDeleteProjectTemplate({ project_id, template_id }) {
  return ResponseAdapter(await del(`/api/v1/project/template?project_id=${project_id}&template_id=${template_id}`));
}

export async function apiGetTenantList(project_id) {
  return ResponseAdapter(await get(`/api/v1/project/tenant/list?project_id=${project_id}`));
}
export async function apiPostTenant({ project_id, email, role }) {
  return ResponseAdapter(await post(`/api/v1/project/tenant`, { project_id, email, role }));
}
export async function apiPutTenant({ project_id, email, role }) {
  return ResponseAdapter(await put(`/api/v1/project/tenant`, { project_id, email, role }));
}
export async function apiDelTenant({ project_id, email }) {
  return ResponseAdapter(await del(`/api/v1/project/tenant?project_id=${project_id}&email=${email}`));
}
export async function apiPutTenantInvite({ project_id, email, role }) {
  return ResponseAdapter(await put(`/api/v1/project/tenant/invite`, { project_id, email, role }));
}

export async function apiGetProjectDataAdapters(project_id) {
  return ResponseAdapter(await get(`/api/v1/project/dataadapter/adapter/list?project_id=${project_id}`));
}
export async function apiGetProjectDataAdapter(project_id, adapter_type) {
  return ResponseAdapter(await get(`/api/v1/project/dataadapter/adapter?project_id=${project_id}&adapter_type=${adapter_type}`));
}
export async function apiGetProjectDataAdapterTools(project_id, adapter_type) {
  return ResponseAdapter(await post('/api/v1/project/dataadapter/adapter/definitions', { project_id, adapter_type }));
}
export async function apiPostProjectDataAdapter(payload) {
  return ResponseAdapter(await post('/api/v1/project/dataadapter/adapter', payload));
}
export async function apiPutProjectDataAdapter(payload) {
  return ResponseAdapter(await put('/api/v1/project/dataadapter/adapter', payload));
}
export async function apiPutProjectDataAdapterEnabled({ project_id, adapter_type, enabled }) {
  return apiPutProjectDataAdapter({ project_id, adapter_type, settings: {}, connection: {}, enabled });
}
export async function apiDelProjectDataAdapter({ project_id, adapter_type, name }) {
  return ResponseAdapter(await del(`/api/v1/project/dataadapter/adapter?project_id=${project_id}&adapter_type=${adapter_type || name}`));
}
export async function apiPostProjectDataAdapterVerify({ project_id, adapter_type }) {
  return ResponseAdapter(await post('/api/v1/project/dataadapter/adapter/verify', { project_id, adapter_type }));
}
export async function apiPostProjectDataAdapterDatabases(payload) {
  return ResponseAdapter(await post('/api/v1/project/dataadapter/adapter/databases', payload));
}
export async function apiPostDataAdapterTool(payload) {
  return ResponseAdapter(await post('/api/v1/project/dataadapter/adapter/tools', payload));
}
export async function apiPutDataAdapterTool(payload) {
  return ResponseAdapter(await put('/api/v1/project/dataadapter/adapter/tools', payload));
}
export async function apiDeleteDataAdapterTool({ project_id, adapter_type, tool_id }) {
  return ResponseAdapter(await del(`/api/v1/project/dataadapter/adapter/tools?project_id=${project_id}&adapter_type=${adapter_type}&tool_id=${tool_id}`));
}
export async function apiPostDataAdapterToolTest(payload) {
  return ResponseAdapter(await post('/api/v1/project/dataadapter/adapter/tools/test', payload));
}
export async function apiPostDataAdapterGenerateTools(payload) {
  return ResponseAdapter(await post('/api/v1/project/dataadapter/adapter/tools/generate', payload));
}
export async function apiGetDataAdapterTables({ project_id, adapter_type }) {
  return ResponseAdapter(await get(`/api/v1/project/dataadapter/adapter/tables?project_id=${project_id}&adapter_type=${adapter_type}`));
}
export async function apiPostDataAdapterTablesDdl(payload) {
  return ResponseAdapter(await post('/api/v1/project/dataadapter/adapter/tables/ddl', payload));
}
/**
 * Tests a Data Export block as a dry run: the source query runs on a sample of rows and
 * the files are written and checked, but nothing is uploaded.
 * @param {{project_id: string, payload: object, variables: Object<string, *>}} body The block's current payload and project variable values.
 * @returns {Promise<object>} Response with { files, rows_total, truncated, columns, sample_rows } or a readable error.
 */
export async function apiPostProjectDataExportTest(body) {
  return ResponseAdapter(await post('/api/v1/project/dataexport/test', body));
}
export async function apiPostDataAdapterDefinitions(payload) {
  return ResponseAdapter(await post('/api/v1/project/dataadapter/adapter/definitions', payload));
}
export async function apiPostDataAdapterExecute(payload) {
  return ResponseAdapter(await post('/api/v1/project/dataadapter/adapter/execute', payload));
}
export async function apiPutProjectDataAdapterTables({ project_id, adapter_type, tables }) {
  return ResponseAdapter(await put(`/api/v1/project/dataadapter/tables`, { project_id, adapter_type, tables }));
}
export async function apiPutProjectDataAdapterSuggestions({ project_id, adapter_type, suggestions }) {
  return ResponseAdapter(await put(`/api/v1/project/dataadapter/suggestions`, { project_id, adapter_type, suggestions }));
}
export async function apiPostProjectDataAdapterGenerateSuggestions({ project_id, adapter_type, language, persona_id, ai_provider, ai_model, count }) {
  return ResponseAdapter(await post('/api/v1/project/dataadapter/generatesuggestions', { project_id, adapter_type, language, persona_id, ai_provider, ai_model, count }));
}
export async function apiGetProjectDataAdapterSqlTables(project_id, adapter_type) {
  return ResponseAdapter(await get(`/api/v1/project/dataadapter/sqltables?project_id=${project_id}&adapter_type=${adapter_type}`));
}
export async function apiGetProjectDataAdapterDatabases({ project_id, adapter_type, config }) {
  return ResponseAdapter(await post(`/api/v1/project/dataadapter/databases`, { project_id, adapter_type, config }));
}

export async function apiGetProjectUserList(project_id, page_number) {
  return ResponseAdapter(await get(`/api/v1/project/user/list?project_id=${project_id}&pagesize=50&pagenumber=${page_number}`));
}
export async function apiGetProjectUser({ project_id, email }) {
  const response = await get(`/api/v1/project/user?project_id=${project_id}&email=${email}`);
  return ResponseAdapter(response);
}
export async function apiPostProjectUser({ project_id, email, password, name, timezone, country, organisation, position, language, flags }) {
  const response = await post(`/api/v1/project/user`, { project_id, email, password, name, timezone, country, organisation, position, language, flags });
  return ResponseAdapter(response);
}
export async function apiPutProjectUser({ project_id, email, name, timezone, country, organisation, position, language, flags }) {
  const response = await put(`/api/v1/project/user`, { project_id, email, name, timezone, country, organisation, position, language, flags });
  return ResponseAdapter(response);
}
export async function apiDelProjectUser({ project_id, email }) {
  return ResponseAdapter(await del(`/api/v1/project/user?project_id=${project_id}&email=${email}`));
}
export async function apiPutDisableProjectUser({ project_id, email, disabled }) {
  return ResponseAdapter(await put(`/api/v1/project/user/disable`, { project_id, email, disabled }));
}

export async function apiGetProjectCheckUser({ project_id, email }) {
  const response = await get(`/api/v1/project/checkuser?project_id=${project_id}&email=${email}`);
  return ResponseAdapter(response);
}
export async function apiPutProjectUserRole({ project_id, email, role }) {
  const response = await put(`/api/v1/project/user/role`, { project_id, email, role });
  return ResponseAdapter(response);
}
export async function apiGetProjectUserFeatures({ project_id, email }) {
  const response = await get(`/api/v1/project/user/features/list?project_id=${project_id}&email=${email}`);
  return ResponseAdapter(response);
}
export async function apiPutDeepResearchStartReport({ project_id, report, notification_frequency, email, name }) {
  const response = await put(`/api/v1/project/user/features/deep-research-report/start?project_id=${project_id}`, { report, notification_frequency, email, name });
  return ResponseAdapter(response);
}
export async function apiPutDeepResearchStopReport({ project_id, report, email }) {
  const response = await put(`/api/v1/project/user/features/deep-research-report/stop?project_id=${project_id}`, { report, email });
  return ResponseAdapter(response);
}
export async function apiGetProjectUserDataAdapters({ project_id, email }) {
  const response = await get(`/api/v1/project/user/dataadapter/list?project_id=${project_id}&email=${email}`);
  return ResponseAdapter(response);
}
export async function apiPostProjectUserDataAdapter({ project_id, adapter_type, config, email }) {
  const response = await post(`/api/v1/project/user/dataadapter`, { project_id, adapter_type, config, email });
  return ResponseAdapter(response);
}
export async function apiDelProjectUserDataAdapter({ project_id, email, adapter_type }) {
  return ResponseAdapter(await del(`/api/v1/project/user/dataadapter?project_id=${project_id}&email=${email}&adapter_type=${adapter_type}`));
}

export async function apiGetProjectFeatures({ project_id }) {
  const response = await get(`/api/v1/project/features?project_id=${project_id}`);
  return ResponseAdapter(response);
}
export async function apiPutProjectFeatures({ project_id, features }) {
  const response = await put(`/api/v1/project/features`, { project_id, features });
  return ResponseAdapter(response);
}
/**
 * Loads the project's encryption key status (owners only).
 * @param {{project_id: string}} payload Project.
 * @returns {Promise<object>} Adapted response with versions, policy, schedule, job and events.
 */
export async function apiGetProjectKeys({ project_id }) {
  return ResponseAdapter(await get(`/api/v1/project/keys?project_id=${project_id}`));
}
/**
 * Saves the project's key rotation policy (owners only).
 * @param {{project_id: string, rotation_days: number, grace_days: number}} payload Policy.
 * @returns {Promise<object>} Adapted response with the saved policy.
 */
export async function apiPutProjectKeyPolicy({ project_id, rotation_days, grace_days }) {
  return ResponseAdapter(await put(`/api/v1/project/keys/policy`, { project_id, rotation_days, grace_days }));
}
/**
 * Rotates the project's encryption key now (owners only).
 * @param {{project_id: string}} payload Project.
 * @returns {Promise<object>} Adapted response with the rotation status and the new key status.
 */
export async function apiPostProjectKeyRotate({ project_id }) {
  return ResponseAdapter(await post(`/api/v1/project/keys/rotate`, { project_id }));
}
export async function apiPutProjectLogo({ project_id, file_name, file_type, file, type }) {
  const response = await put(
    `/api/v1/project/logo?project_id=${project_id}&file_name=${file_name}&file_type=${file_type}`,
    file,
    {
      'Content-Type': type,
      'x-xsrf-token': localStorage.getItem('xsrf-token'),
    },
    false,
  );
  return ResponseAdapter(response);
}
export async function apiPostVariableFile({ project_id, name, dataType, file }) {
  let contentType;
  if (dataType === 'PDF') {
    contentType = 'application/pdf';
  } else if (dataType === 'Image') {
    contentType = 'image/png';
  } else if (dataType === 'DataTable') {
    contentType = 'text/csv';
  }
  const response = await post(
    `/api/v1/project/variable/file?project_id=${project_id}&name=${name}&dataType=${dataType}`,
    file,
    {
      'Content-Type': contentType,
      'x-xsrf-token': localStorage.getItem('xsrf-token'),
    },
    false,
  );
  return ResponseAdapter(response);
}
